import { useState } from "react"

import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { useUpdatePermissions } from "@/features/auth/hooks/useAuth"

interface PermissionToggle {
  key: string
  label: string
  description: string
  enabled: boolean
}

export function MobilePermissionSetupScreen() {
  const navigate = useNavigate()
  const updatePermissionsMutation = useUpdatePermissions()

  const [permissions, setPermissions] = useState<PermissionToggle[]>([
    {
      key: "calendar",
      label: "Calendar access",
      description: "Add rituals and events directly to your schedule and get timely reminders.",
      enabled: true,
    },
    {
      key: "notifications",
      label: "Notifications",
      description: "Stay gently reminded of rituals, streaks, and community updates.",
      enabled: true,
    },
    {
      key: "contacts",
      label: "Contacts access",
      description: "Find and connect with friends who are already part of Rituality.",
      enabled: true,
    },
    {
      key: "location",
      label: "Location access",
      description: "Discover rituals, circles, and gatherings near you.",
      enabled: true,
    },
  ])

  const isLoading = updatePermissionsMutation.isPending

  const togglePermission = (key: string) => {
    setPermissions(prev => prev.map(p => (p.key === key ? { ...p, enabled: !p.enabled } : p)))
  }

  const handleContinue = async () => {
    try {
      const permissionsMap = {
        calendarEnabled: permissions.find(p => p.key === "calendar")?.enabled ?? true,
        notificationsEnabled: permissions.find(p => p.key === "notifications")?.enabled ?? true,
        contactsEnabled: permissions.find(p => p.key === "contacts")?.enabled ?? true,
        locationEnabled: permissions.find(p => p.key === "location")?.enabled ?? true,
      }
      await updatePermissionsMutation.mutateAsync(permissionsMap)
      navigate("/welcome")
    } catch (error) {
      console.error("Permissions update error:", error)
    }
  }

  return (
    <div className="space-y-6 pt-[48px] flex flex-col justify-between h-[calc(100dvh-50px)]">
      <div className="space-y-6">
        <div className="text-center mb-10">
          <h2 className="text-xl font-semibold leading-normal text-[#F2F0EBE6]">
            Enable your journey
          </h2>
        </div>

        <div className="space-y-5">
          {permissions.map(permission => (
            <div key={permission.key} className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1.25 min-w-0 max-w-[250px]">
                <p className="text-sm leading-6.25 font-semibold text-primary">
                  {permission.label}
                </p>
                <p className="text-[13px] text-[#F2F0EB66] leading-5">{permission.description}</p>
              </div>
              <Switch
                checked={permission.enabled}
                onCheckedChange={() => togglePermission(permission.key)}
                disabled={isLoading}
                size="default"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4">
        <Button
          type="button"
          variant="primary"
          size="md"
          className="rounded-full uppercase py-3.75 text-sm font-semibold h-12.5"
          fullWidth
          isLoading={isLoading}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}
